import { GeoJsonLayer, IconLayer, PathLayer, TextLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { ScatterplotLayer } from "@deck.gl/layers";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer } from "@deck.gl/layers";
import type { Feature, FeatureCollection, Polygon, MultiPolygon, LineString, Point } from "geojson";
import type {
  IncidentFeature,
  CampusZoneProperties,
  GistdaHotspot,
  IsochroneResult,
  DistrictBoundary,
  DistrictPoi,
  FloodRainStation,
  FloodWaterStation,
  CctvCamera,
} from "@kmitl/shared";
import type { HeatPoint } from "../sim/trafficSim";

/** Same-origin API base. Empty string means "same origin as the page". */
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export interface ShuttleVehicle {
  id: string;
  line: string;
  lat: number;
  lng: number;
  bearing?: number;
  occupancy?: string;
}

export interface RouteProps { route: string; color: string; label: string }
export interface StopProps { id: string; name: string; lines: string[] }
export interface StationProps { id: string; name: string; system: "BTS" | "MRT"; line: string; code: string }
export interface TransitLineProps { id: string; system: "BTS" | "MRT" | "ART"; line: string; color: string; ref: string }
export interface GateProps { id: string; kind: "gate" | "lift-gate" | "entrance"; name: string | null; nameTh: string | null; named: boolean }
export interface ClassifiedRoadProps { name: string | null; nameEn: string | null; nameTh: string | null; highway: string; priority: number; oneway: boolean }
export interface NeighborhoodBuildingProps { id: string; name: string | null; nameEn: string | null; height: number; levels: number | null; building: string }

// ── Waterways (khlong / canal / drain) ────────────────────────────────────
// Used by the 2D water-network layer + flow-direction arrows. Source:
// apps/web/public/geo/kmitl-waterways.geojson — **verbatim OpenStreetMap
// geometry** (ODbL), pulled with
//   way["waterway"](13.700,100.740,13.760,100.820)
// through the Overpass API. 97 features / ~102 km, of which 20 carry a real
// Thai name (คลองลาดกระบัง, คลองจระเข้ใหญ่, คลองประเวศบุรีรมย์, …).
//
// Two things are NOT from OSM and are labelled as such in the panel:
//   • widthM / depthM  — design cross-sections by waterway class (MWA
//     hydraulic tables), not surveyed values.
//   • flow direction    — OSM carries no `flow_direction` tag here, so the
//     arrows are derived downstream-to-river from the network topology.
export interface WaterwayProperties {
  id: string;
  /** OSM way/relation id — the feature's real identity, for citation. */
  osmId?: number;
  name: string;
  nameTh?: string;
  /** "canal" (khlong/river/stream) or "drain" (storm drain/ditch, รางระบายน้ำ). */
  kind: "canal" | "drain";
  /** Verbatim OSM `waterway` value: river | canal | stream | drain | ditch. */
  osmClass?: string;
  basin?: string;
  /** Local catchment / sub-basin. */
  basinLocal?: string;
  /** Design cross-section width in metres by OSM class (MWA tables). */
  widthM?: number;
  /** Design cross-section depth in metres by OSM class. */
  depthM?: number;
  /** Traced channel length in metres, measured off the OSM geometry. */
  lengthM?: number;
  /** [lng, lat] of the first OSM vertex. */
  upstream?: [number, number];
  /** [lng, lat] of the last OSM vertex. */
  downstream?: [number, number];
  level?: "ground" | "subsurface";
  source?: string;
  tier?: string;
}

export interface FloodProneProperties {
  id: string;
  name: string;
  risk: "low" | "medium" | "high";
  depthCm?: number;
  cause?: string;
  source?: string;
  tier?: string;
}

// Campus-accent hover highlight (brand --accent #F5A623). Translucent so the
// underlying feature stays legible while signalling "this is interactive" the
// instant the cursor lands on it. Paired with autoHighlight on every pickable
// layer below. Edit here once — do not inline per-layer.
const HIGHLIGHT_COLOR: [number, number, number, number] = [245, 166, 35, 120];

const ZONE_COLORS: Record<string, [number, number, number]> = {
  academic: [56, 189, 248],
  residential: [167, 139, 250],
  athletic: [245, 158, 11],
  park: [52, 211, 153],
  commercial: [183, 48, 122],
  service: [122, 132, 151],
  perimeter: [183, 48, 122],
};

type ZoneFeature = Feature<Polygon | MultiPolygon, CampusZoneProperties>;

export function campusBoundaryLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>,
  options: { extruded?: boolean } = {},
) {
  return new GeoJsonLayer({
    id: "campus-boundary",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    extruded: options.extruded ?? false,
    getFillColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [120, 120, 120];
      return [z[0], z[1], z[2], 38] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: ZoneFeature) => {
      const z = ZONE_COLORS[f.properties.zoneType] ?? [200, 200, 200];
      return [z[0], z[1], z[2], 200] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1,
    getElevation: ((f: ZoneFeature) => f.properties.height ?? 0) as unknown as number,
  });
}

export function trafficHeatmapLayer(data: HeatPoint[]) {
  return new HeatmapLayer<HeatPoint>({
    id: "traffic-heatmap",
    data,
    getPosition: (d) => d.position,
    getWeight: (d) => d.weight,
    radiusPixels: 38,
    intensity: 1.2,
    threshold: 0.04,
    aggregation: "SUM",
    colorRange: [
      [56, 189, 248, 0],
      [56, 189, 248, 120],
      [167, 139, 250, 180],
      [245, 158, 11, 210],
      [248, 113, 113, 235],
      [239, 68, 68, 255],
    ],
  });
}

const INCIDENT_COLORS: Record<IncidentFeature["category"], [number, number, number]> = {
  "traffic-accident": [239, 68, 68],
  "traffic-congestion": [245, 158, 11],
  construction: [251, 191, 36],
  flooding: [56, 189, 248],
  waste: [168, 162, 158],
  lighting: [253, 224, 71],
  sidewalk: [167, 139, 250],
  drainage: [125, 211, 252],
  trees: [52, 211, 153],
  other: [148, 163, 184],
};

export function incidentLayer(id: string, data: IncidentFeature[]) {
  return new ScatterplotLayer<IncidentFeature>({
    id,
    data,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => {
      const c = INCIDENT_COLORS[d.category] ?? [200, 200, 200];
      return [...c, d.status === "resolved" ? 90 : 220] as [number, number, number, number];
    },
    getRadius: (d) => (d.severity === "high" ? 26 : d.severity === "medium" ? 18 : 12),
    radiusMinPixels: 4,
    radiusMaxPixels: 22,
    stroked: true,
    getLineColor: [10, 14, 20, 230],
    lineWidthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return [200, 200, 200];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function shuttleRoutesLayer(collection: FeatureCollection<LineString, RouteProps>) {
  return new GeoJsonLayer({
    id: "kmitl-transit-routes",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: ((f: Feature<LineString, RouteProps>) => {
      const c = hexToRgb(f.properties.color);
      return [c[0], c[1], c[2], 220] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 3,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 5,
  });
}

/**
 * Render a single campus shuttle route (one line). Per-line toggles let
 * the user isolate a route to read it; the colour comes from the route's own
 * `color` property (matches the operator's printed legend).
 */
export function shuttleRouteLineLayer(
  routeId: string,
  collection: FeatureCollection<LineString, RouteProps>,
) {
  const feature = collection.features.find((f) => f.properties.route === routeId);
  if (!feature) return null;
  const rgb = hexToRgb(feature.properties.color);
  return new GeoJsonLayer({
    id: `kmitl-transit-line-${routeId}`,
    data: { type: "FeatureCollection", features: [feature] } as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: [rgb[0], rgb[1], rgb[2], 230],
    getLineWidth: 4,
    lineWidthMinPixels: 3,
    lineWidthMaxPixels: 6,
  });
}

export function shuttleStopsLayer(collection: FeatureCollection<Point, StopProps>) {
  return new ScatterplotLayer({
    id: "kmitl-transit-stops",
    data: collection.features,
    getPosition: ((f: Feature<Point, StopProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 18,
    radiusMinPixels: 3,
    radiusMaxPixels: 7,
    getFillColor: [251, 191, 36, 230],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

export function shuttleVehiclesLayer(vehicles: ShuttleVehicle[]) {
  return new ScatterplotLayer<ShuttleVehicle>({
    id: "kmitl-transit-vehicles",
    data: vehicles,
    getPosition: (v) => [v.lng, v.lat],
    getRadius: 38,
    radiusMinPixels: 6,
    radiusMaxPixels: 12,
    getFillColor: [34, 211, 238, 240],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

export function transitStationsLayer(collection: FeatureCollection<Point, StationProps>) {
  return new ScatterplotLayer({
    id: "transit-stations",
    data: collection.features,
    getPosition: ((f: Feature<Point, StationProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 32,
    radiusMinPixels: 5,
    radiusMaxPixels: 10,
    getFillColor: ((f: Feature<Point, StationProps>) =>
      f.properties.system === "BTS"
        ? [56, 189, 248, 240]
        : [96, 165, 250, 240]) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

export interface CuLandProperties {
  id: string;
  name: { en: string; th: string; zh: string };
  kind: "commercial" | "mixed-use" | "athletic" | "park" | "residential" | "healthcare" | "cultural" | "education";
  operator: string;
  color: string;
  describe: string;
}

export function cuLandsLayer(collection: FeatureCollection<Polygon | MultiPolygon, CuLandProperties>) {
  return new GeoJsonLayer({
    id: "cu-lands",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, CuLandProperties>) => {
      const c = hexToRgb(f.properties.color);
      return [c[0], c[1], c[2], 70] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, CuLandProperties>) => {
      const c = hexToRgb(f.properties.color);
      return [c[0], c[1], c[2], 230] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1.5,
    extruded: false,
  });
}

// ─── Campus building footprints (real OSM data) ──────────────────────────
export interface BuildingProperties {
  id: string;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  building: string;
  levels: number | null;
  height: number | null;
  operator: string | null;
}

function buildingHeightMeters(props: BuildingProperties): number {
  if (props.height) return props.height;
  if (props.levels) return props.levels * 3;
  return 9;
}

/**
 * Render every campus building as a filled, tappable polygon.
 * In 2D: low-key footprint outlines (named buildings warmer).
 * In 3D: same polygons extruded to their `height`, with a height-graded
 *        fill so the skyline reads at a glance.
 * In 3DS (substructure): same extrusion at much lower opacity so the
 *        underground utilities below show through — SimCity-2000-style
 *        cutaway. Strokes stay visible at full opacity for context.
 */
export function buildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, BuildingProperties>,
  options: { extruded?: boolean; ghosted?: boolean; energyByBuildingId?: Map<string, { currentKw: number }> } = {},
) {
  const extruded = options.extruded ?? false;
  const ghosted = options.ghosted ?? false;
  const energyMap = options.energyByBuildingId;

  const fillAlpha = ghosted ? 32 : 1.0;
  const lineAlpha = ghosted ? 110 : 220;

  // Height ramp — 4 bands of magenta → amber, named buildings sit on the warmer
  // half so the campus reads warmer than the surrounding city.
  // When energyMap is provided, buildings are colored by kW draw instead.
  const colorFor = (h: number, named: boolean, id: string): [number, number, number, number] => {
    if (energyMap && energyMap.has(id)) {
      const kw = energyMap.get(id)!.currentKw;
      const norm = Math.min(1, kw / 500);
      const alpha = ghosted ? fillAlpha : 235;
      if (norm > 0.8) return [239, 68, 68, alpha];   // red — high load
      if (norm > 0.5) return [234, 179, 8, alpha];   // yellow — elevated
      if (norm > 0.2) return [34, 197, 94, alpha];   // green — normal
      return [20, 184, 166, alpha];                   // teal — idle
    }
    if (extruded) {
      if (h >= 50) return [240, 200, 130, ghosted ? fillAlpha : 235];
      if (h >= 30) return [220, 130, 150, ghosted ? fillAlpha : 220];
      if (h >= 15) return [180, 70, 130,  ghosted ? fillAlpha : 205];
      return       [120, 60, 110,  ghosted ? fillAlpha : 195];
    }
    return named
      ? [183, 48, 122, ghosted ? fillAlpha : 110]
      : [148, 163, 184, ghosted ? fillAlpha : 80];
  };

  return new GeoJsonLayer({
    id: "campus-buildings",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    extruded,
    material: extruded && !ghosted
      ? { ambient: 0.6, diffuse: 0.7, shininess: 12, specularColor: [255, 220, 180] }
      : false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      colorFor(buildingHeightMeters(f.properties), !!f.properties.name, f.properties.id)) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      f.properties.name
        ? ([183, 48, 122, lineAlpha] as [number, number, number, number])
        : ([148, 163, 184, lineAlpha] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: 0.8,
    lineWidthMinPixels: 0.5,
    getElevation: ((f: Feature<Polygon | MultiPolygon, BuildingProperties>) =>
      buildingHeightMeters(f.properties)) as unknown as number,
    opacity: ghosted ? 0.35 : 1,
    updateTriggers: {
      getFillColor: [extruded, ghosted, energyMap],
      getLineColor: [ghosted],
    },
  });
}

// ─── Underground / substructure PathLayer factories ─────────────────────
// Re-render utility line geometry at a negative z so we can see the
// network "buried" beneath the ghosted buildings. Depths are typical Bangkok
// burial: electricity ~2 m, water ~3 m, drainage ~4 m.

function lineFeaturesAt(
  collection: FeatureCollection,
  depthMeters: number,
): Array<{ path: [number, number, number][]; properties: Record<string, unknown> }> {
  const out: Array<{ path: [number, number, number][]; properties: Record<string, unknown> }> = [];
  for (const f of collection.features) {
    if (f.geometry.type !== "LineString") continue;
    const coords = (f.geometry.coordinates as [number, number][]).map(
      (c) => [c[0], c[1], -depthMeters] as [number, number, number],
    );
    out.push({ path: coords, properties: (f.properties ?? {}) as Record<string, unknown> });
  }
  return out;
}

export function electricityPathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 2);
  return [
    new PathLayer({
      id: "cu-electricity-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as ElectricityProps).kind === "hv-backbone"
          ? [245, 158, 11, 255]
          : [253, 186, 116, 230],
      getWidth: (d) => ((d.properties as ElectricityProps).kind === "hv-backbone" ? 14 : 8),
      widthUnits: "pixels",
      widthMinPixels: 4,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
    }),
    new TextLayer({
      id: "cu-electricity-labels-3ds",
      data: paths.filter((d) => ((d.properties as unknown as ElectricityProps).kind === "hv-backbone" || (d.properties as unknown as ElectricityProps).name)),
      getPosition: (d) => {
        const mid = Math.floor(d.path.length / 2);
        return d.path[mid];
      },
      getText: (d) => (d.properties as unknown as ElectricityProps).name || "",
      getSize: 14,
      getColor: [245, 158, 11, 240],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: "monospace",
      fontWeight: "bold",
      characterSet: "auto",
      parameters: { depthTest: false },
      pickable: false,
    }),
  ];
}

export function waterPathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 3);
  return [
    new PathLayer({
      id: "cu-water-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as WaterProps).kind === "main"
          ? [56, 189, 248, 250]
          : [147, 197, 253, 220],
      getWidth: (d) => ((d.properties as WaterProps).kind === "main" ? 12 : 7),
      widthUnits: "pixels",
      widthMinPixels: 4,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
    }),
    new TextLayer({
      id: "cu-water-labels-3ds",
      data: paths.filter((d) => ((d.properties as unknown as WaterProps).kind === "main" || (d.properties as unknown as WaterProps).name)),
      getPosition: (d) => {
        const mid = Math.floor(d.path.length / 2);
        return d.path[mid];
      },
      getText: (d) => {
        const p = d.properties as unknown as WaterProps;
        const parts: string[] = [];
        if (p.name) parts.push(p.name);
        if (p.diameter) parts.push(`Ø${p.diameter}`);
        return parts.join(" ");
      },
      getSize: 13,
      getColor: [56, 189, 248, 235],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: "monospace",
      fontWeight: "bold",
      characterSet: "auto",
      parameters: { depthTest: false },
      pickable: false,
    }),
  ];
}

export function drainagePathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 4);
  return [
    new PathLayer({
      id: "cu-drainage-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: (d) =>
        (d.properties as DrainageProps).kind === "main"
          ? [16, 185, 129, 250]
          : [110, 231, 183, 220],
      getWidth: (d) => ((d.properties as DrainageProps).kind === "main" ? 14 : 8),
      widthUnits: "pixels",
      widthMinPixels: 4,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
    }),
    new TextLayer({
      id: "cu-drainage-labels-3ds",
      data: paths.filter((d) => ((d.properties as unknown as DrainageProps).kind === "main" || (d.properties as unknown as DrainageProps).name)),
      getPosition: (d) => {
        const mid = Math.floor(d.path.length / 2);
        return d.path[mid];
      },
      getText: (d) => {
        const p = d.properties as unknown as DrainageProps;
        const parts: string[] = [];
        if (p.name) parts.push(p.name);
        if (p.diameter) parts.push(`Ø${p.diameter}`);
        if (p.capacityM3) parts.push(`${p.capacityM3}m³`);
        return parts.join(" ");
      },
      getSize: 13,
      getColor: [16, 185, 129, 235],
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      billboard: true,
      fontFamily: "monospace",
      fontWeight: "bold",
      characterSet: "auto",
      parameters: { depthTest: false },
      pickable: false,
    }),
  ];
}

/** SRT / ARL corridor at grade (z ≈ 0) for X-RAY context. */
export function railPathLayer(collection: FeatureCollection) {
  const paths = lineFeaturesAt(collection, 0);
  return [
    new PathLayer({
      id: "kmitl-rail-paths-3ds",
      data: paths,
      getPath: (d) => d.path,
      getColor: [110, 75, 58, 240],
      getWidth: 10,
      widthUnits: "pixels",
      widthMinPixels: 3,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
    }),
    new TextLayer({
      id: "kmitl-rail-labels-3ds",
      data: paths.filter((d) => !!(d.properties as { name?: string }).name),
      getPosition: (d) => d.path[Math.floor(d.path.length / 2)],
      getText: (d) => String((d.properties as { name?: string }).name ?? ""),
      getSize: 13,
      getColor: [110, 75, 58, 235],
      billboard: true,
      fontFamily: "monospace",
      fontWeight: "bold",
      characterSet: "auto",
      pickable: false,
    }),
  ];
}

type FloodStationDatum =
  | (FloodWaterStation & { kind: "wl" })
  | (FloodRainStation & { kind: "rain" });

function floodStationColor(d: FloodStationDatum): [number, number, number, number] {
  if (d.kind === "wl") {
    const lvl = d.situationLevel ?? 3;
    if (lvl >= 5) return [239, 68, 68, 240];
    if (lvl >= 4) return [249, 115, 22, 230];
    return [56, 189, 248, 220];
  }
  const mm = d.rain24h ?? 0;
  if (mm > 90) return [37, 99, 235, 240];
  if (mm > 35) return [59, 130, 246, 230];
  if (mm > 10) return [147, 197, 253, 220];
  return [191, 219, 254, 200];
}

/** HII ThaiWater gauges near campus — WL + rain. */
export function floodWatchStationsLayer(
  water: FloodWaterStation[],
  rain: FloodRainStation[],
) {
  const data: FloodStationDatum[] = [
    ...water.map((s) => ({ ...s, kind: "wl" as const })),
    ...rain.map((s) => ({ ...s, kind: "rain" as const })),
  ];
  return [
    new ScatterplotLayer<FloodStationDatum>({
      id: "flood-watch-stations",
      data,
      getPosition: (d) => [d.lng, d.lat],
      getFillColor: (d) => floodStationColor(d),
      getRadius: (d) => (d.kind === "wl" ? 90 : 70),
      radiusUnits: "meters",
      radiusMinPixels: 6,
      radiusMaxPixels: 18,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
    }),
    new TextLayer<FloodStationDatum>({
      id: "flood-watch-labels",
      data,
      getPosition: (d) => [d.lng, d.lat, 40],
      getText: (d) =>
        d.kind === "wl"
          ? `WL L${d.situationLevel ?? "—"}`
          : `RN ${d.rain24h != null ? `${d.rain24h.toFixed(0)}mm` : "—"}`,
      getSize: 12,
      getColor: [26, 26, 26, 230],
      getTextAnchor: "start",
      getPixelOffset: [10, 0],
      billboard: true,
      fontFamily: "monospace",
      fontWeight: "bold",
      characterSet: "auto",
      pickable: false,
    }),
  ];
}

export interface BmaPoi {
  id: string;
  kind:
    | "hospital"
    | "health-center"
    | "school"
    | "fire-station"
    | "police-station"
    | "park"
    | "market"
    | "bma-office"
    | "flood-gate"
    | "pump-station"
    | "cctv"
    | "bus-stop"
    | "other";
  name: string;
  lat: number;
  lng: number;
  description?: string;
}

const POI_COLORS: Record<BmaPoi["kind"], [number, number, number]> = {
  hospital:        [239, 68, 68],
  "health-center": [248, 113, 113],
  school:          [56, 189, 248],
  "fire-station":  [251, 146, 60],
  "police-station":[59, 130, 246],
  park:            [52, 211, 153],
  market:          [251, 191, 36],
  "bma-office":    [168, 162, 158],
  "flood-gate":    [34, 211, 238],
  "pump-station":  [125, 211, 252],
  cctv:            [229, 231, 235],
  "bus-stop":      [167, 139, 250],
  other:           [148, 163, 184],
};

export function bmaPoiLayer(pois: BmaPoi[]) {
  return new ScatterplotLayer<BmaPoi>({
    id: "bma-pois",
    data: pois,
    getPosition: (p) => [p.lng, p.lat],
    getRadius: (p) => (p.kind === "hospital" ? 28 : p.kind === "fire-station" || p.kind === "police-station" ? 22 : 14),
    radiusMinPixels: 4,
    radiusMaxPixels: 10,
    getFillColor: (p) => {
      const c = POI_COLORS[p.kind] ?? [200, 200, 200];
      return [c[0], c[1], c[2], 230] as [number, number, number, number];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

export function bmaParksLayer(collection: FeatureCollection<Polygon | MultiPolygon, { PARK_NAME_T?: string }>) {
  return new GeoJsonLayer({
    id: "bma-parks",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getFillColor: [52, 211, 153, 50],
    getLineColor: [52, 211, 153, 200],
    getLineWidth: 0.8,
    lineWidthMinPixels: 0.5,
  });
}

export interface AqStation {
  id: string;
  name: string;
  address: string;
  pm25: number | null;
  pm10: number | null;
  lat: number;
  lng: number;
}

export function bmaAqStationsLayer(stations: AqStation[]) {
  return new ScatterplotLayer<AqStation>({
    id: "bma-aq-stations",
    data: stations,
    getPosition: (s) => [s.lng, s.lat],
    getRadius: 60,
    radiusMinPixels: 8,
    radiusMaxPixels: 16,
    getFillColor: (s) => {
      const v = s.pm25 ?? 0;
      if (v < 12) return [34, 197, 94, 255];
      if (v < 35) return [250, 204, 21, 255];
      if (v < 55) return [249, 115, 22, 255];
      if (v < 150) return [239, 68, 68, 255];
      return [127, 29, 29, 255];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 2,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

export function cctvLayer(cameras: CctvCamera[]) {
  return new ScatterplotLayer<CctvCamera>({
    id: "cctv-cameras",
    data: cameras,
    getPosition: (c) => [c.lng, c.lat],
    getRadius: 24,
    radiusMinPixels: 4,
    radiusMaxPixels: 8,
    getFillColor: [229, 231, 235, 220],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

// IconLayer reference, kept so 3D-extruded buildings + vehicle icons can be added later.
export { IconLayer };

// ─── Surrounding buildings (urban fabric ~1km around campus) ─────────────
export interface SurroundingBuildingProperties {
  id: string;
  /** Overture's own primary name. Null when the source has none — never invented. */
  name: string | null;
  /** Overture height in metres, or null when the source has none. */
  height: number | null;
  levels: number | null;
  class: string | null;
  subtype: string | null;
  /**
   * Which upstream dataset this footprint came from. Overture is a composite and
   * the licences differ (OSM records are ODbL), so this is carried per building
   * rather than summarised once for the layer.
   */
  source: string | null;
  distM: number;
  overtureId: string;
}

/**
 * Height in metres, or null when we genuinely do not know.
 *
 * The previous version returned a flat 12 for anything missing a height. With
 * the old file that was 58 fabricated rectangles so it never mattered; against
 * real Overture footprints it would have invented a height for 1,770 of the
 * 1,790 buildings (only 20 carry one) and drawn them all as identical 12 m
 * boxes. Null means "the source does not say", and the layer renders that as a
 * flat footprint.
 */
function surroundingBuildingHeightMeters(props: SurroundingBuildingProperties): number | null {
  if (props.height != null && props.height > 0) return props.height;
  if (props.levels != null && props.levels > 0) return props.levels * 3.2;
  return null;
}

export function surroundingBuildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, SurroundingBuildingProperties>,
  options: { ghosted?: boolean } = {},
) {
  // surrounding-buildings.geojson is REAL Overture Maps geometry for every
  // footprint within 1 km of KMITL — it replaced an older file of 58
  // Chulalongkorn landmarks (25-29 km away) drawn as invented rectangles on a
  // 0.001° grid, which this layer used to have to work around.
  //
  // It stays flat. Only 20 of 1,790 footprints carry a height, so extruding
  // would mean inventing one for the other 1,770. Buildings with a known
  // height get a filled tint; the rest are outline-only, which reads as
  // "footprint known, height unknown" rather than as a flat-roofed shed.
  const ghosted = options.ghosted ?? false;
  const lineAlpha = ghosted ? 60 : 160;

  return new GeoJsonLayer({
    id: "surrounding-buildings",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    extruded: false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, SurroundingBuildingProperties>) => {
      const h = surroundingBuildingHeightMeters(f.properties);
      if (h == null) return [70, 90, 120, 14] as [number, number, number, number];
      const a = ghosted ? 26 : 58;
      if (h >= 100) return [160, 140, 200, a] as [number, number, number, number];
      if (h >= 60) return [120, 130, 180, a] as [number, number, number, number];
      if (h >= 35) return [90, 110, 150, a] as [number, number, number, number];
      return [70, 90, 120, a] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: ((f: Feature<Polygon | MultiPolygon, SurroundingBuildingProperties>) => {
      const h = surroundingBuildingHeightMeters(f.properties);
      // A footprint with no height is drawn in the muted outline tone, which is
      // the same tone the old synthetic file used — but here it distinguishes
      // "no height known" rather than "low building".
      if (h == null) return [70, 90, 120, lineAlpha] as [number, number, number, number];
      if (h >= 100) return [160, 140, 200, lineAlpha] as [number, number, number, number];
      if (h >= 60) return [120, 130, 180, lineAlpha] as [number, number, number, number];
      if (h >= 35) return [90, 110, 150, lineAlpha] as [number, number, number, number];
      return [70, 90, 120, lineAlpha] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 1,
    lineWidthMinPixels: 0.75,
    opacity: ghosted ? 0.3 : 0.85,
    updateTriggers: { getLineColor: [ghosted], getFillColor: [ghosted] },
  });
}

// ─── Bangkok district boundaries ─────────────────────────────────────────
export interface DistrictProperties {
  id: string;
  nameTh: string;
  nameEn: string;
  code: string;
  areaKm2: number;
}

export function districtBoundariesLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, DistrictProperties>,
) {
  return new GeoJsonLayer({
    id: "bangkok-districts",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: [255, 255, 255, 160],
    getLineWidth: 2,
    lineWidthMinPixels: 1.5,
    lineWidthMaxPixels: 3,
  });
}

export function districtLabelsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, DistrictProperties>,
) {
  // Compute centroids for labels
  const labels = collection.features.map((f) => {
    let cx = 0, cy = 0, n = 0;
    const coords = f.geometry.type === "Polygon"
      ? f.geometry.coordinates[0]
      : f.geometry.coordinates[0][0];
    for (const [x, y] of coords) { cx += x; cy += y; n++; }
    return {
      position: [cx / n, cy / n, 0] as [number, number, number],
      text: `${f.properties.nameEn}`,
      sub: f.properties.nameTh,
    };
  });
  return new TextLayer({
    id: "bangkok-district-labels",
    data: labels,
    getPosition: (d) => d.position,
    getText: (d) => d.text,
    getSize: 16,
    getColor: [255, 255, 255, 200],
    getAngle: 0,
    getTextAnchor: "middle",
    getAlignmentBaseline: "center",
    billboard: true,
    fontFamily: "'IBM Plex Sans Condensed', sans-serif",
    fontWeight: "bold",
    characterSet: "auto",
    getBackgroundColor: [0, 0, 0, 120],
    background: true,
    parameters: { depthTest: false },
    pickable: false,
  });
}

// Flood-prone polygons were removed 2026-10-03 along with the hand-drawn
// kmitl-flood-prone.geojson. `FloodAreaProperties` and its risk palette went
// with them — a risk grade with no measured basis is exactly the kind of
// invented data this dashboard does not ship. Flood extent now comes from live
// gauge telemetry (see floodGaugeLayer below), and the static susceptibility
// surface will return only when it is derived from a real DEM.

// ─── Flood watch gauges (live HII telemetry) ─────────────────────────────
// The earlier `flood-prone-areas` layer drew five hand-placed rectangles with
// invented chronic-depth figures. Two of them had byte-identical dimensions,
// which is what exposed them: they were bounding boxes, not flood extents, and
// over real imagery they read as arbitrary blocks. Replaced 2026-10-03.
//
// What draws now is a measurement, not an assertion: every dot is a real HII
// (Hydroinformatics Institute) station published by FloodDash
// (api-flood.nonarkara.org → /api/snapshot), carrying its own coordinates,
// situation level, water level in m MSL, margin to bank, and observation
// time. Nothing here is hand-placed, and a gauge that goes dry simply
// disappears rather than staying on the map as a stale risk zone.
//
// Level → colour follows the Thai national warning scale the source uses:
//   L1–2  normal        teal
//   L3    watch         amber
//   L4    prepare       orange
//   L5    overflow      red, and drawn larger
export interface FloodGaugeProperties {
  /** FloodDash station key (HII oldcode where one exists). */
  key: string;
  nameEn: string;
  nameTh: string;
  provinceEn: string;
  lat: number;
  lng: number;
  distanceKm: number;
  /** 1 (low) … 5 (over bank). */
  situationLevel: number;
  /** Channel storage, % of capacity. >100 means the bank is overtopped. */
  storagePct: number | null;
  /** Water level in metres above mean sea level, where published. */
  wlMsl: number | null;
  /** Metres remaining before the station's bank. Negative = overflowing. */
  marginToBankM: number | null;
  /** FloodDash action band: safe | watch | prepare | evacuate. */
  band: string;
  bandTh: string;
  /** `absent` means the reading is too old to present as current. */
  dataConfidence: "fresh" | "recent" | "stale" | "absent";
  ageMin: number | null;
  observedAt: string | null;
}

const GAUGE_BAND_COLOR: Record<string, [number, number, number, number]> = {
  safe:     [0, 151, 141, 210],    // Wada Benzol Green — normal
  watch:    [245, 158, 11, 220],   // amber — watch
  prepare:  [217, 102, 41, 235],   // Wada English Red (light) / amber-orange
  // Wada deep neg (--neg #b3261e). This was Tailwind's red-500 #ef4444,
  // which is in no plate of this palette; the legend swatch and the map dot
  // have to be the same colour, so both moved to the house red.
  evacuate: [179, 38, 30, 250],    // red — over bank
};

function gaugeColor(level: number, band: string): [number, number, number, number] {
  return (
    GAUGE_BAND_COLOR[band] ??
    (level >= 5 ? GAUGE_BAND_COLOR.evacuate
      : level === 4 ? GAUGE_BAND_COLOR.prepare
      : level === 3 ? GAUGE_BAND_COLOR.watch
      : GAUGE_BAND_COLOR.safe)
  );
}

export function floodGaugeLayer(gauges: FloodGaugeProperties[]) {
  return new ScatterplotLayer<FloodGaugeProperties>({
    id: "flood-gauges",
    data: gauges,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    stroked: true,
    lineWidthMinPixels: 1.5,
    getLineColor: (d) => {
      // An `absent` reading is history, not an alert: hollow grey ring, so it
      // can still be located but never reads as "this is happening now".
      if (d.dataConfidence === "absent") return [148, 163, 184, 210] as [number, number, number, number];
      const c = gaugeColor(d.situationLevel, d.band);
      return [255, 255, 255, 190] as [number, number, number, number];
    },
    getRadius: (d) => {
      if (d.dataConfidence === "absent") return 90;
      return d.situationLevel >= 5 ? 220 : d.situationLevel >= 4 ? 150 : 100;
    },
    radiusUnits: "meters",
    radiusMinPixels: 4,
    radiusMaxPixels: 14,
    getFillColor: (d) => {
      if (d.dataConfidence === "absent") return [30, 36, 46, 120] as [number, number, number, number];
      return gaugeColor(d.situationLevel, d.band);
    },
  });
}

/** Name + level labels for the gauges, so the map is readable without a hover.
 *  Only current (fresh/recent) L4+ readings get a label. Labelling every gauge
 *  would re-create the clutter this layer exists to remove, and labelling a
 *  week-old `absent` reading would advertise a stale emergency. */
export function floodGaugeLabelLayer(gauges: FloodGaugeProperties[]) {
  const labelled = gauges.filter(
    (g) => g.situationLevel >= 4 && g.dataConfidence !== "absent",
  );
  return new TextLayer<FloodGaugeProperties>({
    id: "flood-gauge-labels",
    data: labelled,
    pickable: false,
    getPosition: (d) => [d.lng, d.lat],
    getText: (d) => `${d.nameEn} · L${d.situationLevel}`,
    getSize: 11,
    sizeUnits: "pixels",
    getColor: [255, 255, 255, 235],
    getBackgroundColor: [12, 16, 22, 210],
    background: true,
    backgroundPadding: [4, 2],
    fontFamily: "Archivo Narrow, sans-serif",
    fontWeight: 700,
    getPixelOffset: [0, -16],
    billboard: true,
  });
}

// ─── Waterway network (2D — khlongs, canals, campus drains) ──────────────
// Source: apps/web/public/geo/kmitl-waterways.geojson. Coordinates are
// ordered upstream→downstream so the PathLayer renders in the natural
// flow direction. Three sub-layers stacked from the bottom up:
//   1. base halo   — soft Cerulian Blue band, 22 px — the channel "glow"
//                   that survives any base map and reads as water at any zoom.
//   2. main body   — saturated Cerulian stroke, width ∝ channel width (m).
//   3. labels      — name + width at the line midpoint.
// Drain is rendered as a thinner, slightly different colour so the operator
// can tell main canals from roadside storm drains at a glance.
// Wada plate 1 + 155: surface water = Cerulian Blue #0093a5 (canals),
// Benzol Green #00978d (urban drains). Cerulian is paired with the institute
// orange in the same plate; Benzol is the water/wet signal from plate 155.
// RGB values for deck.gl's getColor (0–255 range).
const WATERWAY_KIND_COLOR: Record<"canal" | "drain", [number, number, number, number]> = {
  canal: [0, 147, 165, 230],     // Wada Cerulian Blue — surface water
  drain: [0, 151, 141, 200],     // Wada Benzol Green — urban stormwater
};

function pathMidpoint(coords: [number, number][]): [number, number] {
  if (coords.length === 0) return [0, 0];
  if (coords.length === 1) return coords[0];
  const i = Math.floor(coords.length / 2);
  return coords[i];
}

/**
 * Visual rank for a channel, from its traced length.
 *
 * This exists because the layer styling was written when the geojson held 10
 * hand-curated features and could afford to treat every line identically. The
 * file is now verbatim OSM — 97 features, of which 48 are drains and many are
 * unnamed stubs of a few hundred metres. Applying one uniform treatment to all
 * of them is what produced the pale banding: a 24 px halo drawn on 97
 * features merges into haze that reads as random stripes rather than water.
 *
 * Thresholds are on traced length, which is a property of the geometry rather
 * than an editorial judgement:
 *   trunk  ≥ 3 km — main khlong. Full halo, labelled, arrow.
 *   main   ≥ 1 km — secondary canal. Thinner halo, label if named.
 *   minor  < 1 km — drain / stub. No halo, no label, no arrow.
 */
type ChannelRank = "trunk" | "main" | "minor";

function channelRank(f: Feature<LineString, WaterwayProperties>): ChannelRank {
  const len = f.properties.lengthM ?? 0;
  if (len >= 3000) return "trunk";
  if (len >= 1000) return "main";
  return "minor";
}

/** True when OSM gave the channel a real name worth putting on the map.
 *
 *  The check is on `nameTh` alone, deliberately. The geojson build fills
 *  `name` with a fallback for unnamed ways (`Canal 129991953` — the OSM way
 *  id), so testing `name` would pass for every feature and put way ids on the
 *  map in a label box. Only an actual OSM name qualifies. */
function isNamed(f: Feature<LineString, WaterwayProperties>): boolean {
  return Boolean(f.properties.nameTh && f.properties.nameTh.trim().length > 0);
}

/** A label is only worth its pixels when the channel is named AND big enough
 *  to be found on screen. Unnamed stubs get no text at all. */
function shouldLabel(f: Feature<LineString, WaterwayProperties>): boolean {
  return isNamed(f) && channelRank(f) !== "minor";
}

/** Channels that get a flow arrow: trunk and main canals, no stubs.
 *
 *  Exported because the arrow layer itself bails out early when no 2D canvas
 *  is available (jsdom, SSR), which would make the filter untestable. Testing
 *  the selection directly is the only way to assert "not every feature gets
 *  an arrow" without a browser. */
export function arrowableFeatures(
  collection: FeatureCollection<LineString, WaterwayProperties>,
): Array<Feature<LineString, WaterwayProperties>> {
  return collection.features.filter((f) => channelRank(f) !== "minor");
}

export function waterwaysLayer(
  collection: FeatureCollection<LineString, WaterwayProperties>,
) {
  const feats = collection.features;

  // 1. Soft edge. NOT a band.
  //
  //    This started at a uniform 24px, was re-ranked to 16px trunk / 9px main,
  //    and both were wrong: on the void basemap a 16px band of Cerulian Blue
  //    is the brightest object on the board, so eleven district canals read as
  //    a grid of arbitrary lines crossing the campus. Confirmed by layer
  //    isolation — switching to a lens without waterways made them vanish.
  //
  //    What it is now is an anti-aliasing cushion: a few pixels wider than the
  //    body, at low alpha, so a 2px channel still resolves against the black
  //    plate. It must never be wider than the body by more than ~2px, or it
  //    stops being a cushion and becomes the thing you are looking at.
  const halo = new PathLayer<Feature<LineString, WaterwayProperties>>({
    id: "waterways-halo",
    data: feats.filter((f) => channelRank(f) !== "minor"),
    getPath: (f) => f.geometry.coordinates as [number, number][],
    getColor: [0, 147, 165, 46],
    getWidth: (f) => (channelRank(f) === "trunk" ? 5 : 3.5),
    widthUnits: "pixels",
    // No `widthMinPixels` floor: the floor was what let a ranked width become
    // a minimum-visible slab on channels far smaller than the viewport.
    widthMinPixels: 1,
    pickable: false,
    jointRounded: true,
    capRounded: true,
  });

  // 2. Main body — width from the channel's own traced cross-section, so a
  //    30 m river reads wider than a 3 m ditch.
  //
  //    The campus is the subject of this board, so channels sit below it in the
  //    hierarchy: they are thin, and their alpha is pulled back from the
  //    nominal 230 so they read as context rather than as the foreground.
  const body = new PathLayer<Feature<LineString, WaterwayProperties>>({
    id: "waterways-body",
    data: feats,
    getPath: (f) => f.geometry.coordinates as [number, number][],
    getColor: (f) => {
      const rank = channelRank(f);
      const base = WATERWAY_KIND_COLOR[f.properties.kind ?? "canal"];
      // Drains and minor stubs sit back so the trunk canals own the read.
      if (rank === "minor") return [base[0], base[1], base[2], 110] as [number, number, number, number];
      return [base[0], base[1], base[2], 205] as [number, number, number, number];
    },
    getWidth: (f) => {
      const rank = channelRank(f);
      if (rank === "minor") return 1.2;
      const w = f.properties.widthM ?? 6;
      // 3–30 m maps to 1.6–4.0 px.
      return Math.max(1.6, Math.min(4, 1.6 + (w / 30) * 2.4));
    },
    widthUnits: "pixels",
    widthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    jointRounded: true,
    capRounded: true,
  });

  // 3. Labels — named channels only. Unnamed stubs used to render as
  //    "Canal 129991953" (the raw OSM way id), which is noise with no
  //    information in it.
  const labels = new TextLayer<Feature<LineString, WaterwayProperties>>({
    id: "waterways-labels",
    data: feats.filter(shouldLabel),
    getPosition: (f) => pathMidpoint(f.geometry.coordinates as [number, number][]),
    getText: (f) => f.properties.nameTh || f.properties.name,
    getSize: (f) => (channelRank(f) === "trunk" ? 12 : 11),
    getColor: (f) =>
      channelRank(f) === "trunk"
        ? [220, 240, 255, 240]
        : [190, 215, 230, 205],
    fontFamily: '"IBM Plex Mono", "Menlo", monospace',
    fontWeight: 600,
    getTextAnchor: "middle",
    getAlignmentBaseline: "center",
    background: true,
    backgroundPadding: [3, 1],
    getBackgroundColor: [10, 14, 20, 200],
    getPixelOffset: [0, -7],
    pickable: false,
    billboard: true,
    sizeUnits: "pixels",
    sizeMinPixels: 9,
    sizeMaxPixels: 13,
  });

  return [halo, body, labels];
}

/** A triangle that points along the line's natural flow direction.
 *  One per LineString, placed at the line midpoint. The triangle's
 *  `getAngle` returns the bearing from the first to the last coordinate
 *  so the arrow always points downstream regardless of how the user
 *  authored the GeoJSON. */
function flowBearing(coords: [number, number][]): number {
  if (coords.length < 2) return 0;
  const a = coords[0];
  const b = coords[coords.length - 1];
  // atan2(dlng, dlat) → bearing in degrees, clockwise from north.
  const dy = b[1] - a[1];
  const dx = b[0] - a[0];
  // Project lng by cos(lat) so a degree of longitude at Lat Krabang ≈ 0.92
  // of a degree of latitude in the distance plane.
  const meanLat = ((a[1] + b[1]) / 2) * Math.PI / 180;
  const adj = dx * Math.cos(meanLat);
  return (Math.atan2(adj, dy) * 180) / Math.PI;
}

export function waterwayFlowArrowsLayer(
  collection: FeatureCollection<LineString, WaterwayProperties>,
) {
  // An arrow is a claim about where water goes, so it belongs only on a
  // channel big enough to carry the claim — not on a 400 m roadside ditch,
  // where 97 triangles read as scattered confetti.
  const arrowed = arrowableFeatures(collection);
  if (arrowed.length === 0) return [];
  // Build a small icon registry on the fly. We use a single 14×14 px
  // triangle drawn into a 2D canvas, encoded as a data-URL PNG. The
  // IconLayer positions + rotates each instance by the line's bearing.
  const size = 14;
  const canvas =
    typeof document !== "undefined" ? document.createElement("canvas") : null;
  let iconUrl: string | null = null;
  if (canvas) {
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, size, size);
      // Pointing right (bearing 0 = north means we draw a "right-pointing"
      // shape here; IconLayer's `getAngle` rotates the whole canvas so
      // we choose a triangle pointing east and let the layer rotate it).
      ctx.beginPath();
      ctx.moveTo(size - 1, 1);
      ctx.lineTo(2, size / 2);
      ctx.lineTo(size - 1, size - 1);
      ctx.closePath();
      ctx.fillStyle = "#0093a5";  // Wada Cerulian Blue — surface water
      ctx.strokeStyle = "#051230";  // Wada Deep Indigo — ink
      ctx.lineWidth = 1.4;
      ctx.fill();
      ctx.stroke();
      iconUrl = canvas.toDataURL("image/png");
    }
  }
  if (!iconUrl) return [];
  return new IconLayer<Feature<LineString, WaterwayProperties>>({
    id: "waterways-flow-arrows",
    data: arrowed,
    getPosition: (f) => {
      const coords = f.geometry.coordinates as [number, number][];
      // Place the arrow ~60% along the line, not at the midpoint, so it
      // doesn't sit on top of the text label.
      const i = Math.floor(coords.length * 0.6);
      return coords[i];
    },
    getIcon: () => ({
      url: iconUrl!,
      width: size,
      height: size,
      anchorY: size / 2,
      mask: false,
    }),
    getSize: 14,
    sizeUnits: "pixels",
    getAngle: (f) => -flowBearing(f.geometry.coordinates as [number, number][]),
    getColor: (f) => {
      const kind = f.properties.kind ?? "canal";
      return kind === "drain" ? [110, 231, 183, 220] : [56, 189, 248, 240];
    },
    pickable: false,
    billboard: true,
  });
}

// ─── Utility layers ──────────────────────────────────────────────────────
// Electricity, water mains, storm drainage, WiFi survey. Geometry comes from
// hand-authored GeoJSONs at /geo/cu-electricity|water|drainage|wifi.geojson —
// see those files for sourcing notes (real substation names verified against
// the CU-MEA SMART CITY agreement; everything else is realistic
// approximation along the road network).

interface ElectricityProps {
  id: string;
  kind: "substation" | "ring-feeder" | "delivery" | "hv-backbone" | "mv-feeder" | "solar-pv" | "battery-storage";
  name: string;
  voltage?: number;
  capacityMva?: number;
  capacityKw?: number;
  capacityMwh?: number;
  status?: string;
  describe?: string;
}

export function electricityLineLayer(collection: FeatureCollection) {
  // Only LineString features (HV backbone, MV feeder).
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-electricity-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: ((f: Feature<LineString, ElectricityProps>) =>
      f.properties.kind === "hv-backbone"
        ? ([245, 158, 11, 240] as [number, number, number, number]) // amber HV
        : ([253, 186, 116, 215] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, ElectricityProps>) =>
      f.properties.kind === "hv-backbone" ? 5 : 2.5) as unknown as number,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 6,
  });
}

export function electricityNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-electricity-nodes",
    data: points,
    getPosition: ((f: Feature<Point, ElectricityProps>) =>
      f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, ElectricityProps>) => {
      const k = f.properties.kind;
      if (k === "substation") return 90;
      if (k === "delivery") return 60;
      if (k === "battery-storage") return 50;
      if (k === "solar-pv") return 45;
      return 35;
    }) as unknown as number,
    radiusMinPixels: 5,
    radiusMaxPixels: 14,
    getFillColor: ((f: Feature<Point, ElectricityProps>) => {
      const k = f.properties.kind;
      if (k === "substation") return [245, 158, 11, 240]; // amber
      if (k === "delivery") return [251, 191, 36, 220];
      if (k === "battery-storage") return [167, 139, 250, 230]; // violet
      if (k === "solar-pv") return [250, 204, 21, 230]; // yellow
      return [253, 186, 116, 220];
    }) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

interface WaterProps {
  id: string;
  kind: "supply-point" | "main" | "lateral" | "fire-hydrant";
  diameter?: number;
  status?: string;
  name?: string;
}

export function waterLineLayer(collection: FeatureCollection) {
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-water-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: ((f: Feature<LineString, WaterProps>) =>
      f.properties.kind === "main"
        ? ([56, 189, 248, 230] as [number, number, number, number]) // cyan main
        : ([147, 197, 253, 200] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, WaterProps>) =>
      f.properties.kind === "main" ? 4 : 2) as unknown as number,
    lineWidthMinPixels: 1.5,
    lineWidthMaxPixels: 5,
  });
}

export function waterNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-water-nodes",
    data: points,
    getPosition: ((f: Feature<Point, WaterProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: 40,
    radiusMinPixels: 4,
    radiusMaxPixels: 10,
    getFillColor: ((f: Feature<Point, WaterProps>) =>
      f.properties.kind === "supply-point"
        ? ([56, 189, 248, 240] as [number, number, number, number])
        : ([147, 197, 253, 230] as [number, number, number, number])) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

interface DrainageProps {
  id: string;
  kind: "main" | "feeder" | "retention-basin" | "outfall" | "pump-station";
  diameter?: number;
  capacityM3?: number;
  flow?: string;
  status?: string;
  name?: string;
}

export function drainageLineLayer(collection: FeatureCollection) {
  const fc = {
    type: "FeatureCollection",
    features: collection.features.filter((f) => f.geometry.type === "LineString"),
  } as FeatureCollection;
  return new GeoJsonLayer({
    id: "cu-drainage-lines",
    data: fc,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: ((f: Feature<LineString, DrainageProps>) =>
      f.properties.kind === "main"
        ? ([16, 185, 129, 230] as [number, number, number, number]) // emerald main
        : ([110, 231, 183, 200] as [number, number, number, number])) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, DrainageProps>) =>
      f.properties.kind === "main" ? 5 : 2.5) as unknown as number,
    lineWidthMinPixels: 2,
    lineWidthMaxPixels: 6,
  });
}

export function drainageNodeLayer(collection: FeatureCollection) {
  const points = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-drainage-nodes",
    data: points,
    getPosition: ((f: Feature<Point, DrainageProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, DrainageProps>) =>
      f.properties.kind === "retention-basin" ? 110 : 50) as unknown as number,
    radiusMinPixels: 5,
    radiusMaxPixels: 16,
    getFillColor: ((f: Feature<Point, DrainageProps>) => {
      const k = f.properties.kind;
      if (k === "retention-basin") return [16, 185, 129, 220];
      if (k === "outfall") return [56, 189, 248, 230];
      if (k === "pump-station") return [110, 231, 183, 230];
      return [16, 185, 129, 220];
    }) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

// ─── WiFi survey heatmap + points ────────────────────────────────────────

interface WifiProps {
  id: string;
  name: string;
  mbps: number;
  rttMs?: number;
  ssid?: string;
  source?: string;
}

// WiFi heatmap, weighted by Mbps (faster = stronger contribution)
export function wifiHeatmapLayer(collection: FeatureCollection) {
  const features = collection.features.filter(
    (f): f is Feature<Point, WifiProps> => f.geometry.type === "Point",
  );
  return new HeatmapLayer<Feature<Point, WifiProps>>({
    id: "cu-wifi-heat",
    data: features,
    getPosition: (f) => f.geometry.coordinates as [number, number],
    getWeight: (f) => f.properties.mbps / 200,
    radiusPixels: 80,
    intensity: 1.2,
    threshold: 0.05,
    aggregation: "MEAN",
    colorRange: [
      [239, 68, 68, 0],
      [239, 68, 68, 120],
      [245, 158, 11, 180],
      [56, 189, 248, 220],
      [34, 197, 94, 240],
    ],
  });
}

/** Pulsing dot at the operator's GPS fix. Two concentric circles + an
 *  accuracy disk underneath. */
export function devicePresenceLayer(
  lng: number,
  lat: number,
  accuracyM: number | null,
) {
  const data = [{ lng, lat, accuracyM }];
  // Convert accuracy metres to a rough pixel radius at zoom 16 (campus zoom).
  // deck.gl Scatterplot uses `radius` in meters when `radiusUnits === "meters"`
  // (the default for this layer). We pass the actual metre value.
  return [
    new ScatterplotLayer({
      id: "device-accuracy",
      data,
      getPosition: (d) => [d.lng, d.lat] as [number, number],
      getRadius: () => accuracyM ?? 30,
      radiusUnits: "meters",
      radiusMinPixels: 8,
      radiusMaxPixels: 240,
      getFillColor: [56, 189, 248, 50],
      stroked: false,
      pickable: false,
    }),
    new ScatterplotLayer({
      id: "device-dot",
      data,
      getPosition: (d) => [d.lng, d.lat] as [number, number],
      getRadius: 16,
      radiusUnits: "pixels",
      getFillColor: [56, 189, 248, 240],
      stroked: true,
      getLineColor: [255, 255, 255, 240],
      lineWidthUnits: "pixels",
      getLineWidth: 2,
      pickable: false,
    }),
  ];
}

export function wifiPointsLayer(collection: FeatureCollection) {
  const features = collection.features.filter((f) => f.geometry.type === "Point");
  return new ScatterplotLayer({
    id: "cu-wifi-points",
    data: features,
    getPosition: ((f: Feature<Point, WifiProps>) =>
      f.geometry.coordinates) as unknown as [number, number],
    getRadius: 22,
    radiusMinPixels: 4,
    radiusMaxPixels: 8,
    getFillColor: ((f: Feature<Point, WifiProps>) => {
      const m = f.properties.mbps;
      if (m >= 120) return [34, 197, 94, 230];   // green — fast
      if (m >= 80)  return [56, 189, 248, 230];  // cyan — ok
      if (m >= 50)  return [245, 158, 11, 230];  // amber — meh
      return [239, 68, 68, 230];                  // red — slow
    }) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

/**
 * Georeferenced raster paper-map overlay. Unused on KMITL (no campus paper map
 * wired); the default bounds fall back to the KMITL Lat Krabang outer extent so
 * no stale central-Bangkok geometry remains. Pass explicit bounds + url to use.
 */
export function cuMapOverlay(
  url = "/maps/campus-map.png",
  bounds: [number, number, number, number] = [100.752, 13.703, 100.802, 13.748],
  opacity = 0.85,
) {
  return new BitmapLayer({
    id: "cu-map-2015",
    image: url,
    bounds,
    opacity,
    pickable: false,
    desaturate: 0,
    // depthTest off so the bitmap stays glued to the ground when buildings
    // are extruded — otherwise the building bottoms occlude it and the map
    // appears to vanish at pitch > 0.
    parameters: { depthTest: false },
  });
}

/** NASA GIBS WMTS tile layer — free, no API key. */
/**
 * NASA GIBS global tile layer (MODIS true-color, NDVI, LST, flood).
 * The source product is GoogleMapsCompatible_Level9 — beyond zoom 9 the same
 * tile is stretched to multiple zoom levels, producing useless smears at
 * city scale. `extent` is left global but `currentZoom` gates the visible
 * prop so the layer simply doesn't render at zoom ≥ 10 by default.
 */
// GIBS product → (format, max-level) heuristic. RGB / true-color products
// ship as JPG at Level 9; thematic palettes (NDVI, LST, AOD, NO2, IMERG)
// ship as PNG at lower max levels.
function inferFormat(productId: string): "jpg" | "png" {
  if (productId.includes("CorrectedReflectance")) return "jpg";
  if (productId.includes("TrueColor")) return "jpg";
  if (productId.includes("DayNightBand")) return "png";
  return "png";
}
function inferLevel(productId: string): 6 | 7 | 8 | 9 {
  if (productId.includes("IMERG")) return 6;
  if (productId.includes("OMI_") || productId.includes("AOD")) return 6;
  if (productId.includes("Land_Surface_Temp")) return 7;
  if (productId.includes("DayNightBand")) return 8;
  return 9;
}

interface GibsOpts {
  /** "jpg" for true-color / RGB products, "png" for thematic / index products. */
  format?: "jpg" | "png";
  /** Tile matrix max level: 9 for high-res, 7-8 for thematic, 6 for IMERG / OMI. */
  level?: 6 | 7 | 8 | 9;
}
export function gibsLayer(
  productId: string,
  date?: string,
  opacity = 0.6,
  currentZoom?: number,
  opts: GibsOpts = {},
) {
  const dateStr = date ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const format = opts.format ?? inferFormat(productId);
  const level = opts.level ?? inferLevel(productId);
  return new TileLayer({
    id: `gibs-${productId}`,
    data:
      `https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/${productId}/default/${dateStr}` +
      `/GoogleMapsCompatible_Level${level}/{z}/{y}/{x}.${format}`,
    minZoom: 0,
    maxZoom: level,
    tileSize: 256,
    opacity,
    visible: currentZoom == null ? true : currentZoom < 10,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/** Esri World Imagery — high-resolution satellite, publicly accessible. */
export function esriSatelliteLayer(opacity = 0.9) {
  return new TileLayer({
    id: "satellite-esri",
    data: "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,
    opacity,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/** OpenTopoMap terrain — for elevation/contour context. Lower max zoom (17). */
export function openTopoTerrainLayer(opacity = 0.6) {
  return new TileLayer({
    id: "satellite-terrain",
    data: [
      "https://a.tile.opentopomap.org/{z}/{x}/{y}.png",
      "https://b.tile.opentopomap.org/{z}/{x}/{y}.png",
      "https://c.tile.opentopomap.org/{z}/{x}/{y}.png",
    ],
    minZoom: 0,
    maxZoom: 17,
    tileSize: 256,
    opacity,
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as string,
        bounds: [w, s, e, n],
      });
    },
  });
}

/**
 * Himawari-9 Band 13 (clean infrared) via NASA GIBS WMS. Geostationary cloud
 * loop, 10-min cadence — best for spotting incoming storms over Bangkok.
 * Uses WMS (not WMTS) because GIBS only exposes Himawari that way.
 */
export function himawariInfraredLayer(opacity = 0.55, currentZoom?: number) {
  const today = new Date().toISOString().slice(0, 10);
  // GIBS WMS template — single tile per request, but TileLayer drives the bbox.
  const wmsBase =
    "https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi?" +
    "SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=TRUE&" +
    `LAYERS=Himawari_AHI_Band13_Clean_Infrared&TIME=${today}&CRS=EPSG:3857&WIDTH=256&HEIGHT=256`;
  return new TileLayer({
    id: "satellite-himawari",
    minZoom: 0,
    maxZoom: 9,
    tileSize: 256,
    opacity,
    visible: currentZoom == null ? true : currentZoom < 10,
    getTileData: async ({ bbox }) => {
      // bbox in Web Mercator metres. GIBS expects BBOX=minx,miny,maxx,maxy.
      const { west, south, east, north } = bbox as { west: number; south: number; east: number; north: number };
      // Convert lat/lng to mercator metres (deck.gl gives lng/lat).
      const R = 6378137;
      const toMx = (lng: number) => (lng * Math.PI * R) / 180;
      const toMy = (lat: number) => Math.log(Math.tan((Math.PI * (90 + lat)) / 360)) * R;
      const url = `${wmsBase}&BBOX=${toMx(west)},${toMy(south)},${toMx(east)},${toMy(north)}`;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        if (typeof createImageBitmap === "function") {
          return await createImageBitmap(blob);
        }
        // Old Safari without createImageBitmap — skip tile rather than leak blob URLs
        return null;
      } catch {
        return null;
      }
    },
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as ImageBitmap,
        bounds: [w, s, e, n],
      });
    },
  });
}

/**
 * Observed flood extent — Copernicus GFM, Sentinel-1 SAR.
 *
 * This is the only layer on the dashboard that answers "where is the water on
 * the ground". Everything else flood-related is a level (HII gauge), a headline
 * (FloodDash) or a context line (the OSM channels). GFM classifies every
 * Sentinel-1 pass with three flood-detection algorithms combined by majority
 * vote at 20 m, within 8 h of acquisition, through cloud and at night.
 *
 * WHY WMS-T AND NOT VECTOR
 * ------------------------
 * GeoServer has WFS disabled for this service, so there are no polygons to
 * fetch — only a tiled raster. Each tile is requested at the tile's own bbox,
 * which is why zooming in resolves detail instead of magnifying a single
 * stretched image: the WMS renders 20 m pixels at whatever scale the current
 * tile covers.
 *
 * WHY IT CAN BE BLANK, AND WHAT THAT MEANS
 * ----------------------------------------
 * Sentinel-1 revisits are ~6 days apart at these latitudes and the orbit
 * geometry skips parts of Thailand. A tile with no water comes back empty.
 * That is reported as "no inundation observed on this date", never as "no
 * flood" — the satellite may simply not have passed. The panel carries the
 * observation date for exactly this reason, and `/api/flood/gfm` only ever
 * hands this layer a date it has verified saw water.
 */
export function gfmFloodExtentLayer(
  observationDate: string | null,
  opacity = 0.85,
): TileLayer | null {
  if (!observationDate) return null;

  // Streamed from OUR api, not from GeoServer.
  //
  // GeoServer advertises `Access-Control-Allow-Origin: *` on the GET, which
  // reads as "the browser can fetch this". Measured in Chrome it cannot: the
  // request is preflighted and the preflight does not allow our origin, so
  // every tile rejects with `TypeError: Failed to fetch`, while the identical
  // URL fetched server-side returns 200 and a valid PNG. NASA GIBS streams from
  // the same page without trouble, so this is that one endpoint.
  //
  // Routing through the API also means the layer works from any origin rather
  // than depending on a third party staying permissive, and the API only
  // serves dates it verified saw water.
  const tileUrl = `${API_BASE}/api/flood/gfm/tile`;

  return new TileLayer({
    id: `gfm-flood-extent-${observationDate}`,
    // Nothing to page through — one raster for the whole date.
    data: observationDate,
    minZoom: 9,
    maxZoom: 17,
    tileSize: 256,
    opacity,
    // GeoServer answers these behind a `GS_FLOW_CONTROL` bucket limited to one
    // concurrent request per anonymous client, and it is a shared public
    // research service. deck.gl defaults to 10 in flight; measured, 8 concurrent
    // GetMap calls do succeed, so this is politeness rather than a fix, and it
    // keeps the burst small enough that no request sits long enough to be
    // aborted by a fast pan.
    maxRequests: 6,
    getTileData: async ({ bbox, signal }) => {
      const { west, south, east, north } = bbox as {
        west: number; south: number; east: number; north: number;
      };
      const qs = new URLSearchParams({
        date: observationDate,
        bbox: `${west},${south},${east},${north}`,
      });
      try {
        const res = await fetch(`${tileUrl}?${qs}`, { signal });
        if (!res.ok) return null;
        const blob = await res.blob();
        // The proxy answers a bad tile with JSON, not an image.
        if (!blob.type.startsWith("image/")) return null;
        if (typeof createImageBitmap !== "function") return null;
        return await createImageBitmap(blob);
      } catch {
        return null;
      }
    },
    renderSubLayers: (props) => {
      const { boundingBox } = props.tile as unknown as {
        boundingBox: [[number, number], [number, number]];
      };
      const [[w, s], [e, n]] = boundingBox;
      return new BitmapLayer({
        ...props,
        data: undefined,
        image: props.data as unknown as ImageBitmap,
        bounds: [w, s, e, n],
      });
    },
  });
}

// ─── AlphaEarth Foundations (Google Satellite Embedding) ────────────────
// AI-derived land cover, change detection, and surface properties at 10 m
// resolution. Generated by scripts/alphaearth-extract.py from the GEE dataset
// GOOGLE/SATELLITE_EMBEDDING/V1/ANNUAL. Each cell is a 10×10 m polygon.
//
// Performance notes (2026-08-29 fat-cut pass):
//   - 22 MB GeoJSON × 3 layers = 66 MB to parse on every load + 100K+
//     polygons to iterate on every pan/zoom + pick buffer scan on every
//     mouse move. At any zoom < 12 a 10 m polygon is sub-pixel, so the
//     layer contributes nothing to the picture but pays the full draw
//     cost. Skip it.
//   - Landcover is the only one with a meaningful tooltip (class name);
//     change + surface are decorative — turn off picking to cut the
//     per-mousemove pickbuffer scan by 2/3.
//   - `visible` is bound to `currentZoom` via `updateTriggers` so it
//     flips on/off as the camera crosses the threshold without rebuilding
//     the layer.

export interface AlphaEarthLandcoverProps {
  class: string;
  classIndex: number;
  embeddingNorm: number;
}

const ALPHAEARTH_LANDCOVER_COLOR: Record<string, [number, number, number, number]> = {
  "built-up":    [186, 140, 120, 200], // warm grey-brown
  "tree-canopy": [ 34, 139,  34, 200], // forest green
  "grass-lawn":  [144, 238, 144, 200], // light green
  "water":       [ 65, 105, 225, 200], // royal blue
  "bare-soil":   [210, 180, 140, 200], // tan
  "road-paved":  [112, 128, 144, 200], // slate
};

const ALPHAEARTH_MIN_ZOOM = 12; // 10 m polygons are sub-pixel below z12 — skip render entirely

export function alphaEarthLandcoverLayer(
  collection: FeatureCollection<Polygon, AlphaEarthLandcoverProps>,
  currentZoom: number,
) {
  return new GeoJsonLayer({
    id: "alphaearth-landcover",
    data: collection as unknown as FeatureCollection,
    stroked: false,
    filled: true,
    pickable: true, // only landcover shows a meaningful tooltip (class name)
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    visible: currentZoom >= ALPHAEARTH_MIN_ZOOM,
    getFillColor: ((f: Feature<Polygon, AlphaEarthLandcoverProps>) =>
      ALPHAEARTH_LANDCOVER_COLOR[f.properties.class] ?? [180, 180, 180, 180]) as unknown as [number, number, number, number],
    getLineColor: [0, 0, 0, 0],
    getLineWidth: 0,
    opacity: 0.85,
    updateTriggers: { visible: [currentZoom] },
  });
}

export interface AlphaEarthChangeProps {
  changeDistance: number;
  changePercentile: number;
  yearFrom: number;
  yearTo: number;
}

export function alphaEarthChangeLayer(
  collection: FeatureCollection<Polygon, AlphaEarthChangeProps>,
  currentZoom: number,
) {
  return new GeoJsonLayer({
    id: "alphaearth-change",
    data: collection as unknown as FeatureCollection,
    stroked: false,
    filled: true,
    pickable: false, // decorative — no tooltip
    visible: currentZoom >= ALPHAEARTH_MIN_ZOOM,
    getFillColor: ((f: Feature<Polygon, AlphaEarthChangeProps>) => {
      const d = f.properties.changeDistance;
      // White (no change) → yellow → orange → red
      if (d < 0.1)  return [255, 255, 255, 100];
      if (d < 0.2)  return [255, 235,  59, 160];
      if (d < 0.35) return [255, 152,   0, 190];
      if (d < 0.5)  return [244,  67,  54, 210];
      return            [183,  28,  28, 230];
    }) as unknown as [number, number, number, number],
    getLineColor: [0, 0, 0, 0],
    getLineWidth: 0,
    opacity: 0.8,
    updateTriggers: { visible: [currentZoom] },
  });
}

export interface AlphaEarthSurfaceProps {
  greenness: number;
  builtUp: number;
}

export function alphaEarthSurfaceLayer(
  collection: FeatureCollection<Polygon, AlphaEarthSurfaceProps>,
  currentZoom: number,
) {
  return new GeoJsonLayer({
    id: "alphaearth-surface",
    data: collection as unknown as FeatureCollection,
    stroked: false,
    filled: true,
    pickable: false, // decorative — no tooltip
    visible: currentZoom >= ALPHAEARTH_MIN_ZOOM,
    getFillColor: ((f: Feature<Polygon, AlphaEarthSurfaceProps>) => {
      const g = f.properties.greenness;
      // Brown (built) → yellow-green → green
      const r = Math.round(180 * (1 - g));
      const gr = Math.round(120 + 135 * g);
      const b = Math.round(60 * (1 - g));
      return [r, gr, b, 200] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineColor: [0, 0, 0, 0],
    getLineWidth: 0,
    opacity: 0.8,
    updateTriggers: { visible: [currentZoom] },
  });
}

// ─── BTS / MRT line geometry ────────────────────────────────────────────
// Polyline tracks for the elevated/underground rail network around the
// campus. Coloured per system: BTS Sukhumvit/Silom (greens), MRT Blue
// (blue), Airport/Gold lines (warm). Lines render below station scatter.

export function transitLinesLayer(collection: FeatureCollection<LineString, TransitLineProps>) {
  return new GeoJsonLayer({
    id: "transit-lines",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getLineColor: ((f: Feature<LineString, TransitLineProps>) => {
      const c = hexToRgb(f.properties.color);
      return [c[0], c[1], c[2], 230] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: 3,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 1.5,
    // Was 7, on a 5px base. At that weight the ARL/SRT line was the heaviest
    // object on the board — a solid band slicing the campus in half — and it
    // reads as one more arbitrary stripe rather than as a railway. It is real
    // geometry, but context must not outrank the campus it runs through.
    lineWidthMaxPixels: 5,
  });
}

// ─── Campus gates / entrances ───────────────────────────────────────────
// Every barrier=gate / barrier=lift_gate / entrance=* node on or near the
// campus perimeter. Named gates (ประตูพญาไท 1/2/3, อังรีดูนังต์ 1/2, ประตูดำ)
// render larger and amber; unnamed minor gates stay small and grey.

export function campusGatesLayer(collection: FeatureCollection<Point, GateProps>) {
  return new ScatterplotLayer({
    id: "campus-gates",
    data: collection.features,
    getPosition: ((f: Feature<Point, GateProps>) => f.geometry.coordinates) as unknown as [number, number],
    getRadius: ((f: Feature<Point, GateProps>) => (f.properties.named ? 36 : 18)) as unknown as number,
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
    getFillColor: ((f: Feature<Point, GateProps>) =>
      f.properties.named
        ? [251, 191, 36, 240]
        : [148, 163, 184, 220]) as unknown as [number, number, number, number],
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

// ─── Road network — classified by priority ──────────────────────────────
// Renders the OSM road network as a visible map element. Width + colour
// scale by `priority`: motorway/primary = thick warm, secondary = medium
// cyan, tertiary = thin cyan, residential/lane = thinnest neutral.

const ROAD_STYLE: Record<number, { color: [number, number, number]; width: number }> = {
  6: { color: [251, 113, 133], width: 4.5 },  // motorway
  5: { color: [251, 146, 60],  width: 3.5 },  // primary / secondary
  4: { color: [125, 211, 252], width: 2.5 },  // tertiary
  3: { color: [148, 163, 184], width: 1.5 },  // residential / lane
  2: { color: [148, 163, 184], width: 1.0 },  // unclassified / minor
};

export function roadNetworkLayer(collection: FeatureCollection<LineString, ClassifiedRoadProps>) {
  return new GeoJsonLayer({
    id: "road-network",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: false,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    // Road network recedes so waterways stay the dominant field. Alpha 90
    // (was 180) drops it to ~half-strength against the base map.
    getLineColor: ((f: Feature<LineString, ClassifiedRoadProps>) => {
      const s = ROAD_STYLE[f.properties.priority] ?? ROAD_STYLE[3];
      return [s.color[0], s.color[1], s.color[2], 90] as [number, number, number, number];
    }) as unknown as [number, number, number, number],
    getLineWidth: ((f: Feature<LineString, ClassifiedRoadProps>) => {
      const s = ROAD_STYLE[f.properties.priority] ?? ROAD_STYLE[3];
      return s.width;
    }) as unknown as number,
    lineWidthUnits: "pixels",
    lineWidthMinPixels: 0.5,
    lineWidthMaxPixels: 4,
  });
}

// ─── Neighborhood tall buildings (≥30 m) ────────────────────────────────
// Skyline context — the towers around Pathumwan / Silom / Ratchaprasong.
// Cooler palette than the campus buildings so the campus visually pops.

export function neighborhoodBuildingsLayer(
  collection: FeatureCollection<Polygon | MultiPolygon, NeighborhoodBuildingProps>,
  options: { extruded?: boolean; ghosted?: boolean } = {},
) {
  const extruded = options.extruded ?? false;
  const ghosted = options.ghosted ?? false;

  const colorFor = (h: number): [number, number, number, number] => {
    const alpha = ghosted ? 32 : extruded ? 215 : 70;
    if (h >= 150) return [186, 230, 253, alpha]; // supertall — pale sky
    if (h >= 80)  return [125, 211, 252, alpha];
    if (h >= 50)  return [56, 189, 248,  alpha];
    return         [71, 85, 105,    alpha];
  };

  return new GeoJsonLayer({
    id: "neighborhood-buildings",
    data: collection as unknown as FeatureCollection,
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    extruded,
    material: extruded && !ghosted
      ? { ambient: 0.55, diffuse: 0.65, shininess: 8, specularColor: [200, 220, 255] }
      : false,
    getFillColor: ((f: Feature<Polygon | MultiPolygon, NeighborhoodBuildingProps>) =>
      colorFor(f.properties.height)) as unknown as [number, number, number, number],
    getLineColor: [125, 211, 252, ghosted ? 90 : 200],
    getLineWidth: 0.6,
    lineWidthMinPixels: 0.4,
    getElevation: ((f: Feature<Polygon | MultiPolygon, NeighborhoodBuildingProps>) =>
      f.properties.height) as unknown as number,
    opacity: ghosted ? 0.35 : 1,
    updateTriggers: {
      getFillColor: [extruded, ghosted],
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// AQ heatmap — IQAir-style PM2.5 gradient overlay
// ─────────────────────────────────────────────────────────────────────────────

export interface AqHeatmapPoint {
  lat: number;
  lng: number;
  aqi: number | null;
  pm25: number | null;
  station: string;
  source?: string;
}

export function aqHeatmapLayer(points: AqHeatmapPoint[]) {
  return new HeatmapLayer({
    id: "aq-heatmap",
    data: points,
    getPosition: (d: AqHeatmapPoint) => [d.lng, d.lat] as [number, number],
    getWeight: (d: AqHeatmapPoint) => {
      const v = d.pm25 ?? (d.aqi != null ? d.aqi / 5 : 0);
      return Math.max(0, v);
    },
    radiusPixels: 140,
    intensity: 1.5,
    threshold: 0.08,
    colorRange: [
      [0, 228, 0, 200],   // green — good
      [255, 255, 0, 200], // yellow — moderate
      [255, 126, 0, 200], // orange — USG
      [255, 0, 0, 210],   // red — unhealthy
      [143, 63, 151, 220],// purple — very unhealthy
      [126, 0, 35, 230],  // maroon — hazardous
    ],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Neighborhood POIs
// ─────────────────────────────────────────────────────────────────────────────

export interface NeighborhoodPoi {
  lat: number;
  lng: number;
  name: string | null;
  nameEn: string | null;
  nameTh: string | null;
  category: "school" | "religious" | "hotel" | "pharmacy" | "cultural" | "bus_stop" | "pier" | "embassy" | "ev" | "retail" | "bank";
  amenity?: string;
  osmId?: string;
}

const POI_COLOR: Record<NeighborhoodPoi["category"], [number, number, number, number]> = {
  school:    [234, 179, 8,   220],
  religious: [139, 92, 246,  220],
  hotel:     [59, 130, 246,  220],
  pharmacy:  [34, 197, 94,   220],
  cultural:  [249, 115, 22,  220],
  bus_stop:  [156, 163, 175, 200],
  pier:      [6, 182, 212,   220],
  embassy:   [239, 68, 68,   220],
  ev:        [16, 185, 129,  220],
  retail:    [168, 85, 247,  220],
  bank:      [234, 179, 8,   200],
};

export function neighborhoodPoisLayer(category: NeighborhoodPoi["category"], pois: NeighborhoodPoi[]) {
  const filtered = pois.filter((p) => p.category === category);
  const color = POI_COLOR[category] ?? [148, 163, 184, 200];
  return new ScatterplotLayer<NeighborhoodPoi>({
    id: `neighborhood-poi-${category}`,
    data: filtered,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: 6,
    radiusUnits: "pixels",
    getFillColor: color,
    getLineColor: [255, 255, 255, 200],
    stroked: true,
    getLineWidth: 1.5,
    lineWidthUnits: "pixels",
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// City report heatmap
// ─────────────────────────────────────────────────────────────────────────────

export function cityReportHeatmapLayer(reports: IncidentFeature[]) {
  return new HeatmapLayer<IncidentFeature>({
    id: "city-report-heatmap",
    data: reports,
    getPosition: (d) => [d.lng, d.lat] as [number, number],
    getWeight: (d) => {
      const sev = d.severity;
      if (sev === "critical") return 4;
      if (sev === "high") return 3;
      if (sev === "medium") return 2;
      return 1;
    },
    radiusPixels: 60,
    intensity: 2,
    threshold: 0.1,
    colorRange: [
      [168, 85, 247, 160],
      [139, 92, 246, 190],
      [239, 68, 68,  210],
      [249, 115, 22, 220],
      [234, 179, 8,  230],
      [255, 255, 255, 240],
    ],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Campus trees
// ─────────────────────────────────────────────────────────────────────────────

export interface TreeProps {
  id?: string;
  species?: string | null;
  genus?: string | null;
  height?: number | null;
  crown_diameter?: number | null;
  crown_radius?: number | null;
  name?: string | null;
}

export function campusTreesLayer(
  collection: FeatureCollection<Point, TreeProps>,
  options: { extruded?: boolean } = {},
) {
  return new ScatterplotLayer<Feature<Point, TreeProps>>({
    id: "campus-trees",
    data: collection.features,
    getPosition: (d) => d.geometry.coordinates as [number, number],
    getRadius: (d) => {
      const r = d.properties.crown_radius ?? (d.properties.crown_diameter ? d.properties.crown_diameter / 2 : null);
      return r != null ? Math.max(1.5, r) : 2.5;
    },
    radiusUnits: "meters",
    getFillColor: options.extruded
      ? [34, 197, 94, 200]
      : [34, 197, 94, 160],
    getLineColor: [16, 185, 129, 200],
    stroked: true,
    getLineWidth: 0.3,
    lineWidthUnits: "meters",
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    updateTriggers: { getFillColor: [options.extruded] },
  });
}

export function woodPolygonsLayer(
  collection: FeatureCollection<Polygon, { id?: string; name?: string | null }>,
) {
  return new GeoJsonLayer({
    id: "campus-wood",
    data: collection as unknown as FeatureCollection,
    stroked: false,
    filled: true,
    pickable: false,
    getFillColor: [34, 197, 94, 70] as [number, number, number, number],
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVED: the Pratumwan (Chulalongkorn) layer family
// ─────────────────────────────────────────────────────────────────────────────
// Thirteen layer builders plus the `ptLinePaths` helper and two local types
// — ~360 lines — built underground-utility, metro and landmark layers for the
// Pratumwan twin, not for this campus. Their GeoJSON was deleted when the
// campus data was swapped, which made them unreachable by construction, and
// every one of them had zero call sites.
//
// They were not obvious dead code, which is why they survived: the strings
// `pratumwanGroundPlane`, `pratumwanSewerLayer` and the rest survive as
// function and layer-id names, so grepping for the DELETED FILES comes back
// clean and the leftovers read as live. Nothing in the app imported them.
//
// The energy MODEL (lib/energyModel.ts) is a different matter and is still
// live — App.tsx and EnergyPanel.tsx both use it. Only its two dead layer
// wrappers went. `PratumwanLandmarkRecord` in that module is likewise still
// live; it is merely misnamed after the wrong campus, which is a rename to
// do deliberately rather than a deletion to smuggle in here.

// ─── GISTDA VIIRS fire hotspots ───────────────────────────────────────────────
// Active fire detections from the VIIRS I-band composite. Each hotspot is a
// satellite pixel (~375 m) flagged as burning. The layer shows intensity via
// brightness temperature and confidence via colour:
//   red    = high confidence  (ef4444)
//   orange = nominal          (fb923c)
//   yellow = low / unknown    (fbbf24)
//
// The endpoint returns an empty feed (fallbackTier:"scenario") until the GISTDA
// API key is upgraded to VIIRS tier. The layer simply renders nothing in that
// state — no code change needed when the key is activated.

function hotspotFillColor(confidence?: string): [number, number, number, number] {
  switch (confidence) {
    case "high":    return [239, 68,  68,  220];
    case "nominal": return [251, 146, 60,  200];
    case "low":     return [251, 191, 36,  160];
    default:        return [251, 146, 60,  180];
  }
}

export function gistdaFireHotspotsLayer(hotspots: GistdaHotspot[]) {
  return new ScatterplotLayer<GistdaHotspot>({
    id: "gistda-fire-hotspots",
    data: hotspots,
    getPosition: (d) => [d.lon, d.lat],
    // Larger radius for high-brightness (active/intense) fire pixels
    getRadius: (d) => (d.brightness != null && d.brightness > 400 ? 900 : 550),
    radiusUnits: "meters",
    radiusMinPixels: 4,
    radiusMaxPixels: 20,
    getFillColor: (d) => hotspotFillColor(d.confidence),
    // Thin white halo to distinguish hotspots from background map colour
    stroked: true,
    getLineColor: [255, 255, 255, 80],
    getLineWidth: 1,
    lineWidthMinPixels: 0.5,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    opacity: 0.9,
    updateTriggers: { getFillColor: [], getRadius: [] },
  });
}

// ── Isochrone layer ───────────────────────────────────────────────────────────

const ISOCHRONE_MODE_COLOR: Record<string, [number, number, number, number]> = {
  walk:                   [59,  130, 246, 60],   // blue
  bicycle:                [16,  185, 129, 60],   // green
  drive:                  [245, 158, 11,  60],   // amber
  approximated_transit:   [139, 92,  246, 60],   // purple
};

/**
 * Renders a Geoapify isochrone polygon on the map.
 * Pass the IsochroneResult from /api/isochrone, or null to hide.
 */
export function isochroneLayer(result: IsochroneResult | null) {
  if (!result) return null;
  const color = ISOCHRONE_MODE_COLOR[result.mode] ?? [59, 130, 246, 60];
  const [r, g, b] = color;
  // IsochroneResult.geometry is a structural Polygon/MultiPolygon union;
  // cast to the geojson package's Geometry so deck.gl's data prop accepts it.
  const geometry = result.geometry as unknown as Polygon | MultiPolygon;
  return new GeoJsonLayer({
    id: "isochrone-polygon",
    data: {
      type: "FeatureCollection" as const,
      features: [{ type: "Feature" as const, geometry, properties: {} }],
    },
    filled: true,
    stroked: true,
    getFillColor: [r, g, b, 55],
    getLineColor: [r, g, b, 200],
    getLineWidth: 2,
    lineWidthMinPixels: 1.5,
    pickable: false,
    updateTriggers: { getFillColor: [result.mode], getLineColor: [result.mode] },
  });
}

// ── Lat Krabang district — toggleable boundary + POIs ─────────────────────

/**
 * Colour palette for the district POI categories. Kept here so the
 * LayerPalette legend and the map renderer stay in sync.
 */
export const DISTRICT_POI_COLORS: Record<DistrictPoi["category"], [number, number, number]> = {
  hospital:   [248, 113, 113], // red — health
  school:     [56, 189, 248],  // cyan — schools
  university: [56, 189, 248],  // cyan — universities grouped with schools
  religious:  [167, 139, 250], // purple — worship sites
  community:  [245, 158, 11],  // amber — community centres
  safety:     [239, 68, 68],   // deep red — fire/police
};

/** Metric keys for the subdistrict zone colouring. */
export type ZoneMetric = "population" | "business" | "risk" | "schools";

/** Per-metric colour ramps (low → high). RGBA tuple, alpha 0..255. */
export const ZONE_METRIC_RAMP: Record<ZoneMetric, Array<[number, number, number]>> = {
  // Population density — soft cyan → deep teal (cool, neutral)
  population: [[224, 242, 254], [125, 211, 252], [56, 189, 248], [14, 116, 144], [8, 47, 73]],
  // Business density — soft amber → deep orange (commerce)
  business:   [[254, 243, 199], [253, 224, 71], [251, 191, 36], [245, 158, 11], [180, 83, 9]],
  // Risk density — soft pink → deep crimson (hazard)
  risk:       [[254, 226, 226], [252, 165, 165], [248, 113, 113], [220, 38, 38], [127, 29, 29]],
  // School capacity (students per subdistrict) — soft mint → deep green
  schools:    [[220, 252, 231], [134, 239, 172], [34, 197, 94], [22, 163, 74], [20, 83, 45]],
};

/** Friendly metric labels + 1-line descriptions for the picker UI. */
export const ZONE_METRIC_LABEL: Record<ZoneMetric, { label: string; describe: string }> = {
  population: { label: "POPULATION",  describe: "Total residents per subdistrict. Cool cyan ramp." },
  business:   { label: "BUSINESSES",  describe: "Number of registered business entities. Amber ramp." },
  risk:       { label: "RISK POINTS", describe: "Reported risk points (fires, accidents, hazards). Red ramp." },
  schools:    { label: "STUDENTS",    describe: "Enrolled students in schools per subdistrict. Green ramp." },
};

export interface SubdistrictZoneProps {
  relationId: number;
  name: string;
  nameEn?: string;
  /** Population per subdistrict (joined from data.go.th by Thai name). */
  population: number;
  /** Business entity count per subdistrict. */
  business: number;
  /** Risk-point count per subdistrict. */
  risk: number;
  /** Student headcount per subdistrict. */
  students: number;
  /** Pre-computed value for the active metric (set in App.tsx before render). */
  value: number;
  /** Metric the fill colour was computed from. */
  metric: ZoneMetric;
}

export interface DistrictBoundaryProps {
  relationId: number;
  name: string;
  nameEn: string;
  pointCount: number;
}

/**
 * District outline drawn as a translucent fill with a crisp 2-px line.
 * The fill is intentionally subtle so the user can still read individual
 * POIs underneath when both layers are on.
 */
export function latKrabangBoundaryLayer(boundary: DistrictBoundary | null | undefined) {
  if (!boundary || boundary.outerRing.length < 4) {
    return null;
  }
  const polygon: Polygon = {
    type: "Polygon",
    coordinates: [boundary.outerRing],
  };
  return new GeoJsonLayer<DistrictBoundaryProps>({
    id: "lat-krabang-boundary",
    data: {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {
            relationId: boundary.relationId,
            name: boundary.name,
            nameEn: boundary.nameEn,
            pointCount: boundary.pointCount,
          },
          geometry: polygon,
        },
      ],
    },
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getFillColor: [183, 48, 122, 28],
    getLineColor: [183, 48, 122, 220],
    getLineWidth: 2,
    lineWidthMinPixels: 1.5,
    lineWidthMaxPixels: 4,
  });
}

/**
 * District POIs (hospitals, schools, religious places, community centres,
 * fire/police) as a single ScatterplotLayer. The category drives colour;
 * use the legend in LayerPalette to identify which dot is which.
 */
export function latKrabangPoiLayer(pois: DistrictPoi[]) {
  if (!pois?.length) return null;
  return new ScatterplotLayer<DistrictPoi>({
    id: "lat-krabang-pois",
    data: pois,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getPosition: (p) => [p.lng, p.lat],
    getRadius: (p) => (p.category === "university" ? 70 : 50),
    radiusMinPixels: 3,
    radiusMaxPixels: 10,
    getFillColor: (p) => {
      const c = DISTRICT_POI_COLORS[p.category] ?? [120, 120, 120];
      return [c[0], c[1], c[2], 220];
    },
    stroked: true,
    getLineColor: [10, 14, 20, 255],
    lineWidthMinPixels: 1,
  });
}

/**
 * Subdistrict zone layer — one GeoJson polygon per khwaeng inside Lat
 * Krabang, fill-coloured by the active metric (population / business /
 * risk / students). Joined client-side from data.go.th stats by Thai
 * name match. The fill ramp runs from the lowest value across the visible
 * subdistricts to the highest, so the user sees relative pressure per
 * turn of the picker.
 */
export function subdistrictZonesLayer(
  zones: Array<SubdistrictZoneProps & { outerRing: Array<[number, number]> }>,
  metric: ZoneMetric,
) {
  if (!zones?.length) return null;
  const ramp = ZONE_METRIC_RAMP[metric];
  const values = zones.map((z) => z.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const span = Math.max(1, maxV - minV);
  // Bucket into 5 ramp stops (quintile-ish).
  return new GeoJsonLayer<SubdistrictZoneProps>({
    id: `lat-krabang-zones-${metric}`,
    data: {
      type: "FeatureCollection",
      features: zones.map((z) => ({
        type: "Feature" as const,
        properties: {
          relationId: z.relationId,
          name: z.name,
          nameEn: z.nameEn,
          population: z.population,
          business: z.business,
          risk: z.risk,
          students: z.students,
          value: z.value,
          metric: z.metric,
        },
        geometry: {
          type: "Polygon" as const,
          coordinates: [z.outerRing],
        },
      })),
    },
    stroked: true,
    filled: true,
    pickable: true,
    autoHighlight: true,
    highlightColor: HIGHLIGHT_COLOR,
    getFillColor: (z) => {
      const norm = (z.properties.value - minV) / span;
      const idx = Math.min(ramp.length - 1, Math.max(0, Math.floor(norm * ramp.length)));
      const c = ramp[idx];
      return [c[0], c[1], c[2], 150];
    },
    getLineColor: [10, 14, 20, 200],
    getLineWidth: 1.5,
    lineWidthMinPixels: 1,
    updateTriggers: {
      getFillColor: [metric, minV, maxV],
    },
  });
}

// ─── Flood area intelligence (FloodDash `emergency` points) ───────────────
//
// The gauge layer above answers "what is the instrument reading". This layer
// answers "where is it reported as flooding, and how confident is that claim",
// which is a different question with a different unit. Keeping them as separate
// layers is deliberate: a district can be `confirmed` on reporting while its
// nearest gauge reads level 3, and collapsing the two would hide which kind of
// evidence you are actually looking at.

export interface FloodAreaProperties {
  key: string;
  areaTh: string | null;
  areaEn: string | null;
  provinceEn: string;
  /** WGS84, [lng, lat] order at render time. */
  lat: number;
  lng: number;
  tier: "confirmed" | "reported" | "sensor";
  score: number | null;
  band: string | null;
  severity: number | null;
  sourceCount: number;
  distanceKm: number;
}

/**
 * Tier → colour. Deliberately not a severity ramp: `confirmed` means evidence
 * agreed, `reported` means only the reporting, `sensor` means only the
 * instrument. An area being *better evidenced* is not the same as being *less
 * flooded*, so these are encoded as separate hues rather than steps of one.
 */
function areaTierColor(tier: FloodAreaProperties["tier"]): [number, number, number, number] {
  if (tier === "confirmed") return [255, 255, 255, 235];
  if (tier === "reported") return [217, 102, 41, 215]; // Wada English Red
  return [148, 163, 184, 190]; // sensor-only: present but unconfirmed
}

export function floodAreaLayer(areas: FloodAreaProperties[]) {
  if (areas.length === 0) return [];
  return [
    new ScatterplotLayer<FloodAreaProperties>({
      id: "flood-areas",
      data: areas,
      pickable: true,
      autoHighlight: true,
      highlightColor: HIGHLIGHT_COLOR,
      stroked: true,
      lineWidthMinPixels: 1.5,
      getLineColor: (d) => [255, 255, 255, 190] as [number, number, number, number],
      // Radius encodes FloodDash's own severity, not a scale invented here.
      getRadius: (d) => 260 + (d.severity ?? 0) * 90,
      radiusMinPixels: 10,
      radiusMaxPixels: 34,
      getFillColor: (d) => {
        const c = areaTierColor(d.tier);
        // A sensor-only point has no coverage behind it; keep the fill faint so
        // a hollow, evidence-thin marker never reads as a solid confirmation.
        return d.tier === "sensor" ? [c[0], c[1], c[2], 70] : [c[0], c[1], c[2], 150];
      },
    }),
    // Label only the places that HAVE a name — a sensor-tier point reports a
    // province, and printing "Nonthaburi" on a point 45 km away would imply a
    // street-level claim the source did not make.
    new TextLayer<FloodAreaProperties>({
      id: "flood-area-labels",
      data: areas.filter((a) => a.areaEn || a.areaTh),
      getPosition: (d) => [d.lng, d.lat] as [number, number],
      getText: (d) => d.areaEn || d.areaTh || "",
      getSize: 12,
      sizeUnits: "pixels",
      sizeMinPixels: 10,
      sizeMaxPixels: 14,
      getColor: (d) => (d.tier === "sensor" ? [148, 163, 184, 200] : [255, 255, 255, 240]),
      getPixelOffset: [0, -22],
      getTextAnchor: "middle",
      getAlignmentBaseline: "bottom",
      background: true,
      backgroundPadding: [4, 2],
      getBackgroundColor: [10, 14, 20, 215],
      fontFamily: '"IBM Plex Mono", "Menlo", monospace',
      fontWeight: 600,
      billboard: true,
      pickable: false,
    }),
  ];
}

